import { useCallback, useRef, useState } from 'react';
import { confetti } from '@tsparticles/confetti';
import { tsParticles } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import {
  type CelebrationOptions,
  CELEBRATION_PRESETS,
  INTENSITY_MULTIPLIERS,
  CELEBRATION_DEFAULTS,
  FIRE_EFFECT_OPTIONS,
} from '@/utils/celebrations';

// Track engine initialization
let engineInitialized = false;
const initializeEngine = async () => {
  if (!engineInitialized) {
    await loadSlim(tsParticles);
    engineInitialized = true;
  }
};

// Unique ID counter for fire effect containers
let fireEffectCounter = 0;

// Store active containers by ID for cleanup
const activeContainers = new Map<string, ReturnType<typeof tsParticles.dom>[0]>();

export interface UseCelebrationReturn {
  /** Fire a one-time confetti burst */
  fire: (options?: Partial<CelebrationOptions>) => void;

  /** Fire the torch ceremony effect (real flames rising upward) */
  fireTorch: (durationSeconds?: number) => Promise<void>;

  /** Start continuous celebration effect (e.g., for winner modal) */
  startContinuous: (options?: Partial<CelebrationOptions>) => void;

  /** Stop continuous celebration effect */
  stopContinuous: () => void;

  /** Whether a continuous effect is currently running */
  isRunning: boolean;
}

/**
 * Hook for celebration effects (confetti and fire particles)
 *
 * @example
 * ```tsx
 * const { fire, fireTorch, startContinuous, stopContinuous } = useCelebration();
 *
 * // One-time confetti burst
 * fire({ type: 'confetti', withSideBursts: true });
 *
 * // Fire torch ceremony (flames rising)
 * fireTorch();
 *
 * // Continuous confetti (e.g., in winner modal)
 * useEffect(() => {
 *   if (isOpen) {
 *     startContinuous({ type: 'confetti', zIndex: 2000 });
 *     return () => stopContinuous();
 *   }
 * }, [isOpen]);
 * ```
 */
export function useCelebration(): UseCelebrationReturn {
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fireEffectIdRef = useRef<string | null>(null);

  /**
   * Fire a one-time celebration burst
   */
  const fire = useCallback((options: Partial<CelebrationOptions> = {}) => {
    const type = options.type ?? CELEBRATION_DEFAULTS.type;
    const intensity = options.intensity ?? CELEBRATION_DEFAULTS.intensity;
    const preset = CELEBRATION_PRESETS[type];
    const multiplier = INTENSITY_MULTIPLIERS[intensity];

    const colors = options.colors ?? [...preset.colors];
    const zIndex = options.zIndex ?? CELEBRATION_DEFAULTS.zIndex;
    const origin = options.origin ?? CELEBRATION_DEFAULTS.origin;

    // Main burst
    confetti({
      particleCount: Math.round(preset.particleCount * multiplier.particleCount),
      spread: preset.spread * multiplier.spread,
      origin,
      colors,
      shapes: [...preset.shapes] as ('circle' | 'square')[],
      gravity: preset.gravity,
      scalar: preset.scalar,
      zIndex,
    });

    // Side bursts if requested
    if (options.withSideBursts) {
      const delay = options.sideBurstDelay ?? CELEBRATION_DEFAULTS.sideBurstDelay;

      setTimeout(() => {
        // Left side burst
        confetti({
          particleCount: Math.round(50 * multiplier.particleCount),
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.7 },
          colors,
          zIndex,
        });

        // Right side burst
        confetti({
          particleCount: Math.round(50 * multiplier.particleCount),
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.7 },
          colors,
          zIndex,
        });
      }, delay);
    }
  }, []);

  /**
   * Fire the torch ceremony effect with real flame particles
   */
  const fireTorch = useCallback(async (durationSeconds: number = 2) => {
    await initializeEngine();

    // Clean up any existing fire effect
    if (fireEffectIdRef.current) {
      const existing = activeContainers.get(fireEffectIdRef.current);
      if (existing) {
        existing.destroy();
        activeContainers.delete(fireEffectIdRef.current);
      }
    }

    // Create unique ID for this effect
    const effectId = `fire-torch-${++fireEffectCounter}`;
    fireEffectIdRef.current = effectId;

    // Load the fire effect
    const container = await tsParticles.load({
      id: effectId,
      options: {
        ...FIRE_EFFECT_OPTIONS,
        emitters: {
          ...(FIRE_EFFECT_OPTIONS.emitters as object),
          life: {
            duration: durationSeconds,
            count: 1,
          },
        },
      },
    });

    // Store container reference for cleanup
    if (container) {
      activeContainers.set(effectId, container);

      // Auto-cleanup after duration + buffer
      setTimeout(() => {
        container.destroy();
        activeContainers.delete(effectId);
        if (fireEffectIdRef.current === effectId) {
          fireEffectIdRef.current = null;
        }
      }, (durationSeconds + 1) * 1000);
    }
  }, []);

  /**
   * Start continuous celebration effect
   */
  const startContinuous = useCallback((options: Partial<CelebrationOptions> = {}) => {
    // Stop any existing continuous effect
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const type = options.type ?? CELEBRATION_DEFAULTS.type;
    const preset = CELEBRATION_PRESETS[type];
    const colors = options.colors ?? [...preset.colors];
    const zIndex = options.zIndex ?? CELEBRATION_DEFAULTS.zIndex;

    // Fire continuous bursts from both sides
    intervalRef.current = setInterval(() => {
      // Left side burst
      confetti({
        particleCount: 8,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.7 },
        colors,
        zIndex,
      });

      // Right side burst
      confetti({
        particleCount: 8,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.7 },
        colors,
        zIndex,
      });
    }, 30);

    setIsRunning(true);
  }, []);

  /**
   * Stop continuous celebration effect
   */
  const stopContinuous = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRunning(false);
  }, []);

  return {
    fire,
    fireTorch,
    startContinuous,
    stopContinuous,
    isRunning,
  };
}
